/* Overview — the signed-in landing page.
   Visual reference: docs/design/overview.html. Normative rules: docs/design/spec/.

   Everything on this page comes from one request (`stats/dashboard/`). The page
   shows every card at once, so splitting it per card would only trade one
   round trip for nine. */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Empty, Input, Spin, Table } from "antd";
import {
  BarChartOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  EnvironmentOutlined,
  FallOutlined,
  IdcardOutlined,
  LeftOutlined,
  MinusOutlined,
  OrderedListOutlined,
  RightOutlined,
  RiseOutlined,
  SearchOutlined,
  SolutionOutlined,
  StopOutlined,
  TeamOutlined,
} from "@ant-design/icons";

import { baseText, baseTooltip, echarts, seriesColor, useChart, useTokens } from "./charts";
import { addMonths, localeFor, monthLabel, monthLabelGenitive, weekdayHeaders } from "../merchandiser/route/dateUtils";
import { call } from "../../util/action";
import { translate } from "../../util/util";
import { OVERVIEW } from "../../config/lang";
import "./overview.scss";

const PAGE_SIZE = 10;

/* Icon and series colour per KPI. The three activity metrics reuse their trend
   series colour so a card and its line agree; the two headcounts share the
   interactive blue. */
const KPI_META = [
  { key: "active_merchandisers", Icon: IdcardOutlined, tone: "action-primary" },
  { key: "active_supervisors", Icon: TeamOutlined, tone: "action-primary" },
  { key: "visits", Icon: EnvironmentOutlined, tone: "series-visits" },
  { key: "reports", Icon: BarChartOutlined, tone: "series-reports" },
  { key: "answers", Icon: SolutionOutlined, tone: "series-answers" },
];

const TREND_SERIES = ["visits", "reports", "answers", "survey_answers"];
const VISIT_STATUSES = ["approved", "on_verification", "not_approved"];
const FUNNEL_STAGES = ["submitted", "reviewed", "approved"];

const ymOf = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
const monthDate = ym => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1);
};

const pct = (part, whole) => (whole ? Math.round((part / whole) * 100) : 0);

/** `{a} of {b}` → the values, without ever inflecting a noun against a count. */
const fill = (template, vars) => Object.keys(vars).reduce((out, k) => out.split(`{${k}}`).join(vars[k]), template);

function computeDelta(current, previous) {
  if (!previous) return { pct: current ? 100 : 0, dir: current ? "up" : "flat" };
  const raw = Math.round(((current - previous) / previous) * 100);
  return { pct: Math.abs(raw), dir: raw > 0 ? "up" : raw < 0 ? "down" : "flat" };
}

/* A signed percentage reads as a direction; an unsigned one reads as a fact. */
const signOf = delta => (delta.dir === "up" ? "+" : delta.dir === "down" ? "-" : "");

/* Completion bands. The thresholds are the mockup's and they drive colour only —
   the number is always shown, so colour never carries the meaning on its own. */
const band = value => (value >= 80 ? "good" : value >= 65 ? "warn" : "bad");

/**
 * Fill in whatever the payload is missing.
 *
 * Every card reads one section of a single response, so one absent section used
 * to take the whole page down: against an API that predates `visit_status` /
 * `surveys` / `photo_review` / `regions`, the first `stats.visit_status.approved`
 * threw and the ErrorBoundary replaced the entire dashboard with an error.
 *
 * Normalising once here, at the boundary, is what keeps that a per-card empty
 * state instead of a blank page — and it means the front end can be deployed
 * before the API change without the page breaking.
 */
function withDefaults(res) {
  const surveys = res.surveys || {};
  return {
    ...res,
    kpis: Object.fromEntries(KPI_META.map(({ key }) => [key, { current: 0, previous: 0, spark: [], ...res.kpis?.[key] }])),
    timeseries: { visits: [], reports: [], answers: [], survey_answers: [], ...res.timeseries },
    visit_status: { approved: 0, on_verification: 0, not_approved: 0, ...res.visit_status },
    photo_review: { submitted: 0, reviewed: 0, approved: 0, rejected: 0, ...res.photo_review },
    regions: { top: [], others: 0, ...res.regions },
    surveys: {
      active: 0,
      total: 0,
      ending_this_week: 0,
      ...surveys,
      answers: { current: 0, previous: 0, ...surveys.answers },
      required: { assigned: 0, answered: 0, unanswered: 0, ...surveys.required },
      by_type: surveys.by_type || [],
      list: surveys.list || [],
    },
    top_merchandisers: res.top_merchandisers || [],
    by_merchandiser: {
      count: 0,
      page: 1,
      page_size: PAGE_SIZE,
      num_pages: 1,
      results: [],
      ...res.by_merchandiser,
    },
  };
}

const initials = name =>
  name
    .split(" ")
    .filter(Boolean)
    .map(w => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

/* ---------------------------------------------------------------- primitives */

function Card({ title, hint, children, className = "" }) {
  return (
    <section className={`ov-card ${className}`}>
      <div className="ov-card__head">
        <h2 className="ov-card__title">{title}</h2>
        {hint ? <span className="ov-card__hint">{hint}</span> : null}
      </div>
      {children}
    </section>
  );
}

/** Twelve months of history as one path. An SVG beats a chart instance here:
    five more ECharts canvases for a 100x28 decoration is not a trade worth making. */
function Sparkline({ values, color }) {
  if (!values || values.length < 2) return null;
  const w = 100;
  const h = 28;
  const pad = 2;
  const min = Math.min(...values);
  const span = Math.max(...values) - min || 1;
  const points = values.map((v, i) => [pad + (i / (values.length - 1)) * (w - pad * 2), h - pad - ((v - min) / span) * (h - pad * 2)]);
  const line = points.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
  return (
    <svg className="ov-spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
      <path d={`${line} L${w - pad} ${h} L${pad} ${h} Z`} fill={color} fillOpacity="0.16" />
      <path d={line} fill="none" stroke={color} strokeWidth="1.6" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function BarList({ rows, color }) {
  const max = Math.max(...rows.map(r => r.value), 1);
  return (
    <div className="ov-bars">
      {rows.map(r => (
        <div className="ov-bar" key={r.name}>
          <span className="ov-bar__name" title={r.name}>
            {r.name}
          </span>
          <span className="ov-bar__val">{r.label}</span>
          <span className="ov-bar__track">
            <span className="ov-bar__fill" style={{ width: `${(r.value / max) * 100}%`, background: color }} />
          </span>
        </div>
      ))}
    </div>
  );
}

/** Donut ring plus a legend that carries the numbers — the ring alone cannot be
    read to a value, and the centre label only has room for one. */
function Donut({ segments, centre, caption, tokens, label, format }) {
  const total = segments.reduce((a, s) => a + s.value, 0);
  const option = useMemo(
    () => ({
      textStyle: baseText(tokens),
      tooltip: {
        ...baseTooltip(tokens),
        trigger: "item",
        formatter: p => `${p.name}<br><b>${format(p.value)}</b> · ${p.percent}%`,
      },
      series: [
        {
          type: "pie",
          radius: ["62%", "88%"],
          center: ["50%", "50%"],
          avoidLabelOverlap: false,
          label: { show: false },
          labelLine: { show: false },
          itemStyle: { borderWidth: 2, borderColor: tokens["surface-page"] },
          emphasis: { scale: true, scaleSize: 4 },
          data: segments.map(s => ({ name: s.name, value: s.value, itemStyle: { color: s.color } })),
        },
      ],
      aria: { enabled: true, label: { description: label } },
    }),
    [segments, tokens, label, format]
  );
  const host = useChart(option);

  return (
    <div className="ov-donut-wrap">
      <div className="ov-donut">
        {/* Sibling, never a child: ECharts takes ownership of its container and
            wipes anything already inside it. */}
        <div className="ov-donut__c" ref={host} />
        <div className="ov-donut__mid">
          <div className="ov-donut__val">{centre}</div>
          <div className="ov-donut__cap">{caption}</div>
        </div>
      </div>
      <div className="ov-breakdown">
        {segments.map(s => (
          <div className="ov-breakdown__row" key={s.name}>
            <i style={{ background: s.color }} />
            {s.name}
            <b>{format(s.value)}</b>
            <span className="ov-breakdown__pct">{pct(s.value, total)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- the page */

export default function OverviewView() {
  const lang = useSelector(state => state.me?.language) || "en";
  const t = useCallback(key => translate(OVERVIEW, key, lang), [lang]);
  const tokens = useTokens();

  const [month, setMonth] = useState(ymOf(new Date()));
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [state, setState] = useState({ stats: null, loading: true, busy: false, error: false });
  const timer = useRef(null);

  const load = useCallback(() => {
    setState(s => ({ ...s, busy: true, error: false }));
    const params = new URLSearchParams({ month, page, page_size: PAGE_SIZE });
    if (search) params.set("search", search);
    call({ method: "GET", url: `stats/dashboard/?${params.toString()}` }, res => {
      if (res && res.kpis) setState({ stats: withDefaults(res), loading: false, busy: false, error: false });
      else setState(s => ({ ...s, loading: false, busy: false, error: true }));
    });
  }, [month, page, search]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const onSearch = e => {
    const { value } = e.target;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setPage(1);
      setSearch(value);
    }, 400);
  };

  const stepMonth = delta => {
    const next = ymOf(addMonths(monthDate(month), delta));
    if (next > ymOf(new Date())) return; // no future months
    setPage(1);
    setMonth(next);
  };

  const { stats, loading, busy, error } = state;
  const current = monthDate(month);
  const atCurrentMonth = month >= ymOf(new Date());

  return (
    <div className="shell-content ov">
      <div className="ov-head">
        <div>
          <h1 className="ov-title">{t("title")}</h1>
          {/* The stepper already names the month, so the subtitle earns its place by
              naming what the deltas on this page are measured against. */}
          <p className="ov-sub">
            {fill(t("period_sub"), {
              m: monthLabel(current, lang),
              /* `period_sub` is "{m} · проти {p}" — the slot after the preposition takes the genitive. */
              p: monthLabelGenitive(addMonths(current, -1), lang),
            })}
          </p>
        </div>
        <div className="ov-monthnav">
          <Button type="text" shape="circle" size="small" icon={<LeftOutlined />} disabled={busy} onClick={() => stepMonth(-1)} aria-label={t("prev_month")} />
          <span className="ov-monthnav__label">{monthLabel(current, lang)}</span>
          <Button
            type="text"
            shape="circle"
            size="small"
            icon={<RightOutlined />}
            disabled={busy || atCurrentMonth}
            onClick={() => stepMonth(1)}
            aria-label={t("next_month")}
          />
        </div>
      </div>

      {loading ? (
        <div className="ov-state">
          <Spin /> {t("loading")}
        </div>
      ) : null}

      {!loading && error ? (
        <div className="ov-state">
          {t("error")}{" "}
          <Button type="link" onClick={load}>
            {t("retry")}
          </Button>
        </div>
      ) : null}

      {!loading && !error && stats ? (
        <Dashboard stats={stats} t={t} lang={lang} tokens={tokens} month={month} busy={busy} page={page} onPage={setPage} onSearch={onSearch} />
      ) : null}
    </div>
  );
}

function Dashboard({ stats, t, lang, tokens, month, busy, page, onPage, onSearch }) {
  const current = monthDate(month);
  const previous = addMonths(current, -1);
  const surveys = stats.surveys;
  const table = stats.by_merchandiser;

  /* Numbers follow the app language, not the browser's — the two disagree on
     every machine where someone picked a language by hand. */
  const n = useMemo(() => {
    const formatter = new Intl.NumberFormat(localeFor(lang));
    return value => formatter.format(value || 0);
  }, [lang]);

  /* ---- trend: four daily series over the month -------------------------- */
  const trendOption = useMemo(() => {
    const [year, m] = month.split("-").map(Number);
    const days = new Date(year, m, 0).getDate();
    const labels = Array.from({ length: days }, (_, i) => i + 1);
    const byDay = key => {
      const map = {};
      (stats.timeseries[key] || []).forEach(r => {
        map[Number(r.date.slice(8, 10))] = r.count;
      });
      return labels.map(d => map[d] || 0);
    };
    const series = TREND_SERIES.map(key => ({ key, data: byDay(key) }));
    /* Biggest series first so its translucent fill sits behind the smaller ones
       rather than washing them out. */
    series.sort((a, b) => Math.max(...b.data, 0) - Math.max(...a.data, 0));

    return {
      textStyle: baseText(tokens),
      legend: {
        top: 0,
        right: 0,
        /* Fixed order. `series` is re-sorted below for paint order, and without
           this the legend would reshuffle itself whenever the data changes. */
        data: TREND_SERIES.map(t),
        icon: "roundRect",
        itemWidth: 9,
        itemHeight: 9,
        itemGap: 14,
        textStyle: baseText(tokens),
      },
      grid: { top: 34, right: 12, bottom: 26, left: 52 },
      tooltip: {
        ...baseTooltip(tokens),
        trigger: "axis",
        axisPointer: { type: "line", lineStyle: { color: tokens["border-subtle"], width: 1 } },
        valueFormatter: n,
      },
      xAxis: {
        type: "category",
        data: labels,
        boundaryGap: false,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { ...baseText(tokens), interval: (i, v) => Number(v) === 1 || Number(v) % 5 === 0 },
      },
      yAxis: {
        type: "value",
        splitLine: { lineStyle: { color: tokens["chart-grid"] } },
        axisLabel: { ...baseText(tokens), formatter: n },
      },
      series: series.map((s, i) => {
        const color = seriesColor(tokens, s.key);
        return {
          name: t(s.key),
          type: "line",
          symbol: "circle",
          symbolSize: 6,
          showSymbol: false,
          z: i + 1,
          lineStyle: { width: 2, color },
          itemStyle: { color },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: `${color}42` },
              { offset: 1, color: `${color}00` },
            ]),
          },
          emphasis: { focus: "series" },
          data: s.data,
        };
      }),
    };
  }, [stats, tokens, t, month, n]);
  const trendHost = useChart(trendOption);

  /* ---- heatmap: the same visit series on a month calendar --------------- */
  const heatOption = useMemo(() => {
    const data = (stats.timeseries.visits || []).map(r => [r.date, r.count]);
    const counts = data.map(d => d[1]);
    /* nameMap is indexed from Sunday no matter what firstDay says, so the
       Monday-first headers have to be rotated. */
    const mon2sun = weekdayHeaders(lang);
    return {
      textStyle: baseText(tokens),
      tooltip: {
        ...baseTooltip(tokens),
        formatter: p => `${p.value[0]}<br><b>${n(p.value[1])}</b> ${t("visits").toLowerCase()}`,
      },
      visualMap: {
        type: "piecewise",
        min: Math.min(...counts, 0),
        max: Math.max(...counts, 1),
        /* splitNumber generates the buckets. Passing `pieces` without a min and
           max on each entry collapses everything into one colour. */
        splitNumber: 5,
        inRange: {
          color: [tokens["brand-50"], tokens["brand-200"], tokens["brand-400"], tokens["brand-600"], tokens["brand-800"]],
        },
        orient: "horizontal",
        right: 8,
        bottom: 4,
        itemWidth: 14,
        itemHeight: 10,
        itemGap: 4,
        showLabel: false,
        text: [t("more"), t("fewer")],
        textStyle: baseText(tokens),
      },
      calendar: {
        top: 22,
        left: 48,
        right: 24,
        bottom: 46,
        /* 'auto' width needs `right` set, or it has no rect to fill. A month has
           only ~6 week columns, so across a full-width card the cells stretch to
           130px and read as bars — hence the max-width on the host, which also
           lets them shrink on a phone instead of being clipped. */
        cellSize: ["auto", 30],
        range: month,
        splitLine: { show: false },
        itemStyle: {
          color: tokens["surface-sunken"],
          borderWidth: 3,
          borderColor: tokens["surface-page"],
        },
        yearLabel: { show: false },
        monthLabel: { show: false },
        /* firstDay lives under dayLabel, not on calendar — set on the calendar
           itself it is silently ignored and weeks stay Sunday-first. */
        dayLabel: { nameMap: [mon2sun[6], ...mon2sun.slice(0, 6)], firstDay: 1, ...baseText(tokens) },
      },
      series: [
        {
          type: "heatmap",
          coordinateSystem: "calendar",
          data,
          itemStyle: { borderWidth: 3, borderColor: tokens["surface-page"] },
          emphasis: { itemStyle: { borderColor: tokens["chart-axis"] } },
        },
      ],
      aria: { enabled: true, label: { description: t("visit_intensity") } },
    };
  }, [stats, tokens, t, lang, month, n]);
  const heatHost = useChart(heatOption);

  /* ---- derived card data ------------------------------------------------
     The two donut segment lists are memoised because they are the dependency
     ECharts' setOption keys off: rebuilt every render, the ring would be
     re-configured on every keystroke in the table's search box. */
  const visitSegments = useMemo(
    () =>
      VISIT_STATUSES.map(key => ({
        name: t(key),
        value: stats.visit_status[key] || 0,
        color: seriesColor(tokens, key),
      })),
    [stats, t, tokens]
  );
  const visitTotal = visitSegments.reduce((a, s) => a + s.value, 0);

  const typeSegments = useMemo(
    () => surveys.by_type.filter(r => r.count > 0).map(r => ({ name: t(`t_${r.type}`), value: r.count, color: seriesColor(tokens, r.type) })),
    [surveys, t, tokens]
  );
  const typeTotal = typeSegments.reduce((a, s) => a + s.value, 0);
  /* The donut's centre carries the largest type and its share — a count of how
     many types exist would be a fact about the chart, not about the data. */
  const topType = typeSegments.reduce((a, s) => (s.value > a.value ? s : a), { value: 0, name: "" });

  const funnelTop = stats.photo_review.submitted || 1;
  const answersDelta = computeDelta(surveys.answers.current, surveys.answers.previous);
  const completion = pct(surveys.required.answered, surveys.required.assigned);

  const regionRows = [
    ...stats.regions.top.map(r => ({ name: r.name, value: r.visits, label: n(r.visits) })),
    ...(stats.regions.others ? [{ name: t("other_regions"), value: stats.regions.others, label: n(stats.regions.others) }] : []),
  ];

  const columns = [
    {
      title: t("col_merchandiser"),
      dataIndex: "name",
      render: name => (
        <span className="ov-who">
          <span className="ov-avatar">{initials(name)}</span>
          {name}
        </span>
      ),
    },
    ...["visits", "reports", "answers", "surveys"].map(key => ({
      title: t(`col_${key}`),
      dataIndex: key,
      align: "right",
      className: "ov-num",
      render: v => n(v),
    })),
    {
      title: t("col_completion"),
      dataIndex: "completion",
      align: "right",
      className: "ov-num",
      // null means "no survey covered any point they visited" — an em dash, not 0%.
      render: v =>
        v == null ? (
          "—"
        ) : (
          <span className="ov-compl">
            <span className="ov-compl__track">
              <span className="ov-compl__fill" style={{ width: `${v}%` }} />
            </span>
            {v}%
          </span>
        ),
    },
  ];

  return (
    <div className={busy ? "ov-body ov-body--busy" : "ov-body"}>
      {/* ---- KPIs ---- */}
      <section className="ov-grid-kpi">
        {KPI_META.map(({ key, Icon, tone }) => {
          const item = stats.kpis[key] || { current: 0, previous: 0, spark: [] };
          const d = computeDelta(item.current, item.previous);
          const DirIcon = d.dir === "up" ? RiseOutlined : d.dir === "down" ? FallOutlined : MinusOutlined;
          const color = tokens[tone] || `var(--${tone})`;
          return (
            <article className="ov-card ov-kpi" key={key}>
              <div className="ov-kpi__top">
                <span className="ov-kpi__icon" style={{ color, background: `${color}1a` }}>
                  <Icon />
                </span>
                <span className={`ov-chip ov-chip--${d.dir}`}>
                  <DirIcon />
                  {d.pct}%
                </span>
              </div>
              <div>
                <div className="ov-kpi__value">{n(item.current)}</div>
                <div className="ov-kpi__label">{t(key)}</div>
              </div>
              <Sparkline values={item.spark} color={color} />
            </article>
          );
        })}
      </section>

      {/* ---- Survey band ---- */}
      <Card title={t("surveys")} hint={t("active_this_month")}>
        <div className="ov-band">
          <div className="ov-band__cell">
            <span className="ov-band__label">
              <OrderedListOutlined /> {t("active_surveys")}
            </span>
            <span className="ov-band__value">{surveys.active}</span>
            <span className="ov-band__hint">{fill(t("active_surveys_hint"), { t: surveys.total, e: surveys.ending_this_week })}</span>
          </div>
          <div className="ov-band__cell">
            <span className="ov-band__label">
              <CheckCircleOutlined /> {t("completion_rate")}
            </span>
            <span className="ov-band__value">{completion}%</span>
            <span className="ov-band__bar">
              <span className="ov-band__fill" style={{ width: `${completion}%` }} />
            </span>
            <span className="ov-band__hint">
              {fill(t("completion_rate_hint"), {
                a: n(surveys.required.answered),
                b: n(surveys.required.assigned),
              })}
            </span>
          </div>
          <div className="ov-band__cell">
            <span className="ov-band__label">
              <SolutionOutlined /> {t("answers_collected")}
            </span>
            <span className="ov-band__value">{n(surveys.answers.current)}</span>
            <span className="ov-band__hint">
              {fill(t("vs_previous_month"), {
                p: `${signOf(answersDelta)}${answersDelta.pct}`,
                m: monthLabelGenitive(previous, lang),
              })}
            </span>
          </div>
          <div className="ov-band__cell">
            <span className="ov-band__label">
              <ExclamationCircleOutlined /> {t("required_unanswered")}
            </span>
            <span className="ov-band__value ov-band__value--danger">{n(surveys.required.unanswered)}</span>
            <span className="ov-band__hint">
              {fill(t("required_unanswered_hint"), {
                p: pct(surveys.required.unanswered, surveys.required.assigned),
              })}
            </span>
          </div>
        </div>
      </Card>

      {/* ---- Activity + visit status ---- */}
      <section className="ov-grid-2-1">
        <Card title={t("trend_title")}>
          <div className="ov-chart ov-chart--tall" ref={trendHost} />
        </Card>
        <Card title={t("visit_status")} hint={`${n(visitTotal)} ${t("total")}`}>
          {visitTotal ? (
            <Donut
              segments={visitSegments}
              centre={`${pct(stats.visit_status.approved, visitTotal)}%`}
              caption={t("approved")}
              tokens={tokens}
              label={t("visit_status")}
              format={n}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("empty")} />
          )}
        </Card>
      </section>

      {/* ---- Photo review / answer types / regions ---- */}
      <section className="ov-grid-3">
        <Card title={t("photo_pipeline")}>
          {stats.photo_review.submitted ? (
            <>
              <div className="ov-funnel">
                {FUNNEL_STAGES.map(key => {
                  const value = stats.photo_review[key] || 0;
                  const share = (value / funnelTop) * 100;
                  return (
                    <div className="ov-funnel__row" key={key}>
                      <div className="ov-funnel__meta">
                        {t(key)} <b>{n(value)}</b>
                        <span className="ov-funnel__pct">{share.toFixed(1)}%</span>
                      </div>
                      <div className="ov-funnel__bar">
                        <div className="ov-funnel__fill" style={{ width: `${share}%`, background: seriesColor(tokens, key) }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="ov-note">
                <StopOutlined className="ov-note__icon" />
                <span>
                  {fill(t("rejected_hint"), {
                    n: n(stats.photo_review.rejected),
                    p: pct(stats.photo_review.rejected, stats.photo_review.reviewed),
                  })}
                </span>
              </div>
            </>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("empty")} />
          )}
        </Card>

        <Card title={t("answers_by_type")} hint={`${n(typeTotal)} ${t("total")}`}>
          {typeSegments.length ? (
            <Donut
              segments={typeSegments}
              centre={`${pct(topType.value, typeTotal)}%`}
              caption={topType.name}
              tokens={tokens}
              label={t("answers_by_type")}
              format={n}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("empty")} />
          )}
        </Card>

        <Card title={t("visits_by_region")} hint={t("top_regions")}>
          {regionRows.length ? (
            <BarList rows={regionRows} color={tokens["brand-500"]} />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("empty")} />
          )}
        </Card>
      </section>

      {/* ---- Completion by survey + top merchandisers ---- */}
      <section className="ov-grid-13-1">
        <Card title={t("completion_by_survey")} hint={t("active_this_month")}>
          {surveys.list.length ? (
            <div className="ov-slist">
              {surveys.list.map(s => (
                <div className="ov-slist__row" key={s.id}>
                  <span className="ov-slist__name" title={s.name}>
                    {s.name}
                  </span>
                  <span className={`ov-slist__pct ov-slist__pct--${band(s.completion)}`}>{s.completion}%</span>
                  <span className="ov-slist__track">
                    <span className={`ov-slist__fill ov-slist__fill--${band(s.completion)}`} style={{ width: `${s.completion}%` }} />
                  </span>
                  <span className="ov-slist__meta">
                    {fill(t("survey_meta"), {
                      a: n(s.answers),
                      q: s.questions,
                    })}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("empty")} />
          )}
        </Card>

        <Card title={t("top_merchandisers")} hint={t("by_visits")}>
          {stats.top_merchandisers.length ? (
            <BarList
              rows={stats.top_merchandisers.map(m => ({
                name: m.name,
                value: m.visits,
                label: n(m.visits),
              }))}
              color={tokens["brand-500"]}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("empty")} />
          )}
        </Card>
      </section>

      {/* ---- Heatmap ---- */}
      <Card title={t("visit_intensity")} hint={`${t("per_day")} · ${monthLabel(current, lang)}`}>
        <div className="ov-chart ov-chart--tall" ref={heatHost} />
      </Card>

      {/* ---- Table ---- */}
      <Card
        title={t("by_merchandiser")}
        hint={
          <Input
            className="ov-search"
            allowClear
            prefix={<SearchOutlined />}
            placeholder={t("search_ph")}
            defaultValue=""
            onChange={onSearch}
            aria-label={t("search_ph")}
          />
        }>
        <Table
          rowKey="id"
          size="middle"
          columns={columns}
          dataSource={table.results}
          loading={busy}
          scroll={{ x: 720 }}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("empty")} /> }}
          pagination={{
            current: page,
            pageSize: table.page_size,
            total: table.count,
            showSizeChanger: false,
            onChange: onPage,
          }}
        />
      </Card>
    </div>
  );
}
